import LiquidText, { liquidTone } from '../components/LiquidText';
import { LiquidDialog } from '../components/LiquidUI';
import DeleteIcon from '../components/DeleteIcon';
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

/* ============================================================
   OBJETIVOS E INDICADORES (SGI — ISO 9001/45001, 6.2/9.1)
   Estrutura fiel ao RG.07: objetivos operacionais por processo,
   indicador + meta, e monitorização por período (medições).
   ============================================================ */

// Quantos registos de medição se esperam num ano, pela frequência declarada.
// O RG.07 tem quatro colunas trimestrais, por isso mesmo um indicador seguido
// mensalmente é registado por trimestre — o mensal conta como quatro.
/* Um período pode estar fechado sem ter sido medido — o valor é este marcador
   e a razão fica no comentário da medição. Conta como período tratado, não como
   medição, para o registo não dizer mais do que aconteceu. */
const NAO_MEDIDO = 'não medido';
const eNaoMedido = (m) => (m.valor || '').trim().toLowerCase() === NAO_MEDIDO;

/* Os períodos que uma frequência implica, pelos nomes usados no registo.
   O periodosEsperados devolve só quantos são; para fechar é preciso saber quais. */
const periodosDe = (freq) => {
    switch ((freq || '').toLowerCase()) {
        case 'anual': return ['Anual'];
        // nos semestrais o registo sempre usou 1ºT/2ºT para 1.º e 2.º semestre
        case 'semestral': return ['1ºT', '2ºT'];
        default: return ['1ºT', '2ºT', '3ºT', '4ºT'];
    }
};

/* Um período só se fecha depois de terminar. Nos semestrais o rótulo 1ºT é o
   1.º semestre, que fecha a 30 de junho. */
const fimDoPeriodo = (p, ano, freq) => {
    const semestral = (freq || '').toLowerCase() === 'semestral';
    const mes = { '1ºT': semestral ? 6 : 3, '2ºT': semestral ? 12 : 6, '3ºT': 9, '4ºT': 12, 'Anual': 12 }[p] || 12;
    return new Date(ano, mes, 0, 23, 59, 59);   // dia 0 do mês seguinte = último dia do mês
};
const periodoTerminou = (p, ano, freq) => fimDoPeriodo(p, ano, freq) < new Date();

const periodosEsperados = (freq) => {
    switch ((freq || '').toLowerCase()) {
        case 'anual': return 1;
        case 'semestral': return 2;
        default: return 4;
    }
};

/* ============================================================
   PERSPETIVA — vai ou não ser cumprido, à data de hoje
   ============================================================

   A meta é texto livre ("≥90%", "<5", ">75% A", "0", "Mantida, sem NC
   maiores"). Lê-se o operador e o número; o que não tiver exatamente um de cada
   é uma meta qualitativa e não se inventa um gráfico para ela.

   O estado sai da ÚLTIMA medição com número, comparada com a meta, e da
   tendência das anteriores:
     - Cumpre       a última cumpre, com folga, e a tendência não a leva para fora;
     - Em risco     cumpre, mas no limite (a menos de 2% da meta), ou a tendência
                    projetada até ao fim do ano sai da meta;
     - Fora da meta a última não cumpre;
     - Sem dados    já terminaram períodos e não há um único número — nem
                    registado, nem fechado com medição. É o caso mais comum, e o
                    mais importante de mostrar: não se sabe se vai ser cumprido;
     - Aguarda      ainda não terminou nenhum período (típico dos anuais);
     - Qualitativa  a meta não é um número.

   Os períodos e as datas em que terminam são os mesmos que a página já usa
   para fechar medições (periodosDe / fimDoPeriodo), para as duas partes da
   página nunca discordarem sobre o que já devia estar medido. */

// "96,05%" → 96.05 · "4.134.784,00" → 4134784 · "100% A" → 100 · "12.5" → 12.5
const paraNumero = (txt) => {
    const m = String(txt || '').match(/-?\d[\d.,]*/);
    if (!m) return null;
    let s = m[0].replace(/[.,]$/, '');
    if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
    else if (!/^-?\d+\.\d{1,2}$/.test(s)) s = s.replace(/\./g, '');
    const v = parseFloat(s);
    return Number.isFinite(v) ? v : null;
};

const lerMeta = (meta) => {
    const s = String(meta || '').trim();
    if (!s) return null;
    const ops = s.match(/≥|>=|≤|<=|>|<|=/g) || [];
    if (ops.length > 1) return { composta: true };   // compostas: "a) ≥60% b) ≥80%"
    const alvo = paraNumero(s);
    if (alvo == null) return null;                   // qualitativas
    let op = ops[0] || null;
    if (op === '>=') op = '≥';
    if (op === '<=') op = '≤';
    if (!op) {
        if (alvo === 0) op = '≤';                    // "0" acidentes, "0" reclamações
        else return null;                            // um número sozinho não diz o sentido
    }
    // o texto tal como foi escrito, para se mostrar: "0" e não "≤0", ">75% A" e não ">75%"
    return { op, alvo, unidade: s.includes('%') ? '%' : '', texto: s.replace(/\s+/g, ' ') };
};

const cumpreMeta = (v, { op, alvo }) =>
    op === '≥' ? v >= alvo : op === '>' ? v > alvo : op === '≤' ? v <= alvo : op === '<' ? v < alvo : v === alvo;

const fmtNum = (v, unidade = '') =>
    `${Number(v).toLocaleString('pt-PT', { maximumFractionDigits: 2 })}${unidade}`;

const ESTADOS_PERSPETIVA = {
    cumpre:      { label: 'Cumpre',           icone: '✓', cor: '#0ca30c', fundo: '#ecfdf3' },
    risco:       { label: 'Em risco',         icone: '!', cor: '#fab219', fundo: '#fffbeb' },
    fora:        { label: 'Fora da meta',     icone: '✕', cor: '#d03b3b', fundo: '#fef2f2' },
    semdados:    { label: 'Sem dados',        icone: '?', cor: '#ec835a', fundo: '#fff7ed' },
    aguarda:     { label: 'Aguarda medição',  icone: '…', cor: '#94a3b8', fundo: '#f8fafc' },
    qualitativa: { label: 'Meta qualitativa', icone: '◇', cor: '#94a3b8', fundo: '#f8fafc' },
    composta:    { label: 'Meta composta',    icone: '◇', cor: '#94a3b8', fundo: '#f8fafc' },
};

const perspetivaDe = (o, hoje = new Date()) => {
    const meta = lerMeta(o.meta);
    const todas = o.medicoes || [];
    const doAno = (per) => fimDoPeriodo(per, o.ano, o.frequencia);

    // os lugares do eixo: os períodos da frequência, e os que tenham sido
    // registados com outro nome (um anual medido a meio do ano, por exemplo)
    const nomes = [...new Set([...periodosDe(o.frequencia), ...todas.map(m => m.periodo)])];
    const lugares = nomes.map(nome => ({ nome, fim: doAno(nome) })).sort((a, b) => a.fim - b.fim);

    const pontos = todas
        .filter(m => !eNaoMedido(m))
        .map(m => ({ periodo: m.periodo, v: paraNumero(m.valor), texto: m.valor, fim: doAno(m.periodo) }))
        .filter(x => x.v != null)
        .sort((a, b) => a.fim - b.fim);

    const terminados = periodosDe(o.frequencia).filter(per => doAno(per) < hoje);
    const porPeriodo = Object.fromEntries(todas.map(m => [m.periodo, m]));
    const semNumero = terminados.filter(per => !pontos.some(x => x.periodo === per));
    const porRegistar = semNumero.filter(per => !porPeriodo[per]);
    const fechadosSem = semNumero.filter(per => porPeriodo[per] && eNaoMedido(porPeriodo[per]));

    const base = { meta, lugares, pontos, semNumero, porRegistar, fechadosSem };

    if (!meta) {
        return { ...base, estado: 'qualitativa',
            razao: 'A meta não é um número, por isso não há como a comparar com as medições.' };
    }
    /* Uma meta com várias partes ("a) ≥60% b) ≥80%") é mensurável, só não cabe
       numa linha. Sem números e com períodos terminados continua a ser "sem
       dados" — é essa a informação que interessa; com números, compara-se à mão. */
    if (meta.composta) {
        if (!pontos.length && terminados.length) {
            return { ...base, meta: null, estado: 'semdados',
                razao: `Já terminaram ${terminados.length} período(s) e não há nenhum valor medido. `
                    + 'A meta tem várias partes (a), b)…) e não é possível dizer se vai ser cumprida.' };
        }
        return { ...base, meta: null, estado: 'composta',
            razao: 'A meta tem várias partes (a), b)…): compara-se cada uma à mão com as medições.' };
    }
    if (!pontos.length) {
        if (!terminados.length) {
            return { ...base, estado: 'aguarda',
                razao: 'Ainda não terminou nenhum período de medição deste ano.' };
        }
        const partes = [];
        if (porRegistar.length) partes.push(`${porRegistar.join(', ')} por registar`);
        if (fechadosSem.length) partes.push(`${fechadosSem.join(', ')} fechado(s) sem medição`);
        return { ...base, estado: 'semdados',
            razao: `Já terminaram ${terminados.length} período(s) e não há nenhum valor medido (${partes.join('; ')}). `
                + 'Não é possível dizer se a meta vai ser cumprida.' };
    }

    const ultimo = pontos[pontos.length - 1];
    const metaTxt = meta.texto;
    const atrasoTxt = semNumero.filter(per => doAno(per) > ultimo.fim).length
        ? ` Depois dela há período(s) sem número: ${semNumero.filter(per => doAno(per) > ultimo.fim).join(', ')}.` : '';

    if (!cumpreMeta(ultimo.v, meta)) {
        return { ...base, estado: 'fora',
            razao: `A última medição (${ultimo.periodo}: ${ultimo.texto}) não cumpre a meta ${metaTxt}.${atrasoTxt}` };
    }

    // tendência: reta pelos pontos medidos, levada até ao último período do ano
    if (pontos.length >= 2) {
        const ix = (per) => lugares.findIndex(l => l.nome === per);
        const xs = pontos.map(x => ix(x.periodo)), ys = pontos.map(x => x.v);
        const mx = xs.reduce((a, b) => a + b, 0) / xs.length;
        const my = ys.reduce((a, b) => a + b, 0) / ys.length;
        const den = xs.reduce((a, x) => a + (x - mx) ** 2, 0);
        const inclinacao = den ? xs.reduce((a, x, i) => a + (x - mx) * (ys[i] - my), 0) / den : 0;
        const noFim = ys[ys.length - 1] + inclinacao * ((lugares.length - 1) - xs[xs.length - 1]);
        if (!cumpreMeta(noFim, meta)) {
            return { ...base, estado: 'risco', projecao: noFim,
                razao: `A última medição (${ultimo.periodo}: ${ultimo.texto}) cumpre ${metaTxt}, mas a tendência `
                    + `leva-a para cerca de ${fmtNum(noFim, meta.unidade)} no fim do ano — fora da meta.${atrasoTxt}` };
        }
    }

    const folga = Math.abs(ultimo.v - meta.alvo);
    if (meta.alvo !== 0 && folga < Math.abs(meta.alvo) * 0.02) {
        return { ...base, estado: 'risco',
            razao: `A última medição (${ultimo.periodo}: ${ultimo.texto}) cumpre ${metaTxt}, mas por muito pouco.${atrasoTxt}` };
    }
    const tend = pontos.length >= 2
        ? (pontos[pontos.length - 1].v === pontos[pontos.length - 2].v ? ' Estável face à anterior.'
            : ((pontos[pontos.length - 1].v > pontos[pontos.length - 2].v) === ['≥', '>'].includes(meta.op)
                ? ' A melhorar face à anterior.' : ' Pior do que a anterior, mas ainda dentro.'))
        : '';
    return { ...base, estado: 'cumpre',
        razao: `A última medição (${ultimo.periodo}: ${ultimo.texto}) cumpre a meta ${metaTxt}.${tend}${atrasoTxt}` };
};

/* O chip do veredicto: ícone e rótulo, sempre os dois — a cor sozinha não
   chega (e o amarelo do "em risco" nem se lê sobre branco). O texto fica na
   tinta normal; só o ícone leva a cor do estado. A seta, quando há, diz para
   onde andaram as duas últimas medições face à meta. */
const SETAS = { melhor: { s: '↗', t: 'a melhorar' }, pior: { s: '↘', t: 'a piorar' }, estavel: { s: '→', t: 'estável' } };

const ChipPerspetiva = ({ estado, titulo, tendencia }) => {
    const e = ESTADOS_PERSPETIVA[estado];
    const seta = tendencia && SETAS[tendencia];
    return (
        /* Ocupa a largura toda da faixa do ano que tem por baixo, com os mesmos
           cantos arredondados: o veredicto e os períodos leem-se como uma peça só,
           e não como um rótulo solto por cima de um gráfico. */
        <span data-liquid-tone={liquidTone(e.fundo)} data-liquid-badge="true" title={titulo} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px 4px 7px',
            width: '100%', boxSizing: 'border-box',
            borderRadius: 7, background: e.fundo, color: '#1e293b', border: `1px solid ${e.cor}40`,
            fontSize: '0.76rem', fontWeight: 700, whiteSpace: 'nowrap', lineHeight: 1.2,
        }}>
            <span aria-hidden="true" style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 15, height: 15, borderRadius: '50%', background: e.cor, color: '#fff',
                fontSize: '0.62rem', fontWeight: 900,
            }}><LiquidText>{e.icone}</LiquidText></span>
            <LiquidText>{e.label}</LiquidText>
            {seta && <span title={seta.t} style={{ color: '#475569', fontWeight: 800, marginLeft: 'auto' }}>{seta.s}</span>}
        </span>
    );
};

/* Para onde andaram as duas últimas medições, no sentido da meta: num "≥"
   subir é melhorar, num "<" é piorar. */
const tendenciaDe = (p) => {
    if (!p.meta || p.meta.composta || p.pontos.length < 2) return null;
    const [a, b] = p.pontos.slice(-2).map(x => x.v);
    if (a === b) return 'estavel';
    return (b > a) === ['≥', '>'].includes(p.meta.op) ? 'melhor' : 'pior';
};

/* Os períodos do ano, lado a lado, cada um com o que se sabe dele:
     - verde com ✓   medido e cumpre a meta
     - vermelho com ✕ medido e não cumpre
     - branco com ?   já terminou e não tem número (é uma falta, não um zero)
     - cinzento a encher  é o período em que estamos — enche até hoje
     - cinzento claro  ainda não começou
   Por cima, a marca de hoje. É isto que responde a "como vai o ano" mesmo
   quando não há um único número — que é o caso de metade dos objetivos. */
const TILES = {
    cumpre: { fundo: '#e7f6e7', borda: '#0ca30c', simbolo: '✓', corSimbolo: '#0ca30c' },
    falha:  { fundo: '#fdeaea', borda: '#d03b3b', simbolo: '✕', corSimbolo: '#d03b3b' },
    medido: { fundo: '#eef2f7', borda: '#94a3b8', simbolo: '',  corSimbolo: '#475569' },
    sem:    { fundo: '#ffffff', borda: '#ec835a', simbolo: '?', corSimbolo: '#ec835a' },
    curso:  { fundo: '#f1f5f9', borda: '#cbd5e1', simbolo: '',  corSimbolo: '#64748b' },
    futuro: { fundo: '#f8fafc', borda: '#e8edf3', simbolo: '',  corSimbolo: '#94a3b8' },
};

const nomeCurto = (nome, freq) => {
    const f = (freq || '').toLowerCase();
    if (f === 'semestral') return nome === '1ºT' ? '1.º sem.' : '2.º sem.';
    if (nome === 'Anual') return 'Ano';
    return nome.replace('ºT', '.º T');
};

const periodosDoAno = (o, p, hoje) => {
    const nomes = periodosDe(o.frequencia);
    let inicio = new Date(o.ano, 0, 1);
    return nomes.map(nome => {
        const fim = fimDoPeriodo(nome, o.ano, o.frequencia);
        const ini = inicio;
        inicio = new Date(fim.getTime() + 1000);
        // as medições deste período: pelo nome, ou — registadas com outro nome,
        // como um anual medido "1ºT" — pela data em que o período delas acaba
        const aqui = (o.medicoes || []).filter(m => m.periodo === nome || (!nomes.includes(m.periodo) && (() => {
            const d = fimDoPeriodo(m.periodo, o.ano, (o.frequencia || '').toLowerCase() === 'semestral' ? 'semestral' : 'trimestral');
            return d >= ini && d <= fim;
        })()));
        const numericas = aqui.filter(m => !eNaoMedido(m)).map(m => ({ ...m, v: paraNumero(m.valor) })).filter(m => m.v != null);
        const ultima = numericas[numericas.length - 1] || null;
        const fechado = aqui.find(eNaoMedido) || null;
        let estado;
        if (ultima) estado = p.meta && !p.meta.composta ? (cumpreMeta(ultima.v, p.meta) ? 'cumpre' : 'falha') : 'medido';
        else if (fim < hoje) estado = 'sem';
        else if (ini <= hoje) estado = 'curso';
        else estado = 'futuro';
        const decorrido = estado === 'curso' ? Math.max(0, Math.min(1, (hoje - ini) / (fim - ini))) : null;
        return { nome, curto: nomeCurto(nome, o.frequencia), ini, fim, estado, ultima, fechado, decorrido };
    });
};

const dataCurta = (d) => d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' });

const AnoDoObjetivo = ({ o, p, hoje = new Date() }) => {
    const periodos = periodosDoAno(o, p, hoje);
    const inicioAno = new Date(o.ano, 0, 1), fimAno = new Date(o.ano, 11, 31, 23, 59, 59);
    const noAno = hoje >= inicioAno && hoje <= fimAno;
    const posHoje = noAno ? (hoje - inicioAno) / (fimAno - inicioAno) : null;
    const metaTxt = p.meta && !p.meta.composta ? p.meta.texto : null;

    const dica = (t) => {
        const quando = `${dataCurta(t.ini)} a ${dataCurta(t.fim)}`;
        if (t.estado === 'cumpre') return `${t.nome}: ${t.ultima.valor} — cumpre a meta ${metaTxt}`;
        if (t.estado === 'falha') return `${t.nome}: ${t.ultima.valor} — não cumpre a meta ${metaTxt}`;
        if (t.estado === 'medido') return `${t.nome}: ${t.ultima.valor}`;
        if (t.estado === 'sem') return `${t.nome} (${quando}): terminou sem medição`
            + (t.fechado?.comentario ? ` — ${t.fechado.comentario}` : t.fechado ? ' (fechado sem valor)' : ' (por registar)');
        if (t.estado === 'curso') {
            const dias = Math.max(0, Math.ceil((t.fim - hoje) / 86400000));
            return `${t.nome} (${quando}): em curso — faltam ${dias} dia(s)`;
        }
        return `${t.nome} (${quando}): ainda não começou`;
    };

    return (
        <div style={{ width: 204 }}>
            {/* a marca de hoje, por cima da faixa do ano */}
            <div style={{ position: 'relative', height: 9 }}>
                {posHoje != null && (
                    <span title={`Hoje, ${hoje.toLocaleDateString('pt-PT')}`} style={{
                        position: 'absolute', left: `calc(${(posHoje * 100).toFixed(1)}% - 5px)`, top: 0,
                        width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
                        borderTop: '7px solid #1e293b',
                    }} />
                )}
            </div>
            <div style={{ display: 'flex', gap: 3 }}>
                {periodos.map(t => {
                    const s = TILES[t.estado];
                    const fundo = t.estado === 'curso'
                        ? `linear-gradient(90deg, #e2e8f0 ${(t.decorrido * 100).toFixed(0)}%, ${s.fundo} ${(t.decorrido * 100).toFixed(0)}%)`
                        : s.fundo;
                    return (
                        <div key={t.nome} title={dica(t)} style={{
                            flex: 1, minWidth: 0, height: 38, borderRadius: 6, position: 'relative',
                            background: fundo, border: `1px solid ${s.borda}`,
                            borderTop: `3px solid ${s.borda}`,
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            boxSizing: 'border-box', padding: '0 2px',
                        }}>
                            <span style={{
                                fontSize: '0.74rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.1,
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%',
                            }}>
                                {t.ultima
                                    ? fmtNum(t.ultima.v, p.meta?.unidade || '')
                                    : s.simbolo
                                        ? <span style={{ color: s.corSimbolo, fontWeight: 900 }}>{s.simbolo}</span>
                                        : <span style={{ color: '#cbd5e1' }}>·</span>}
                            </span>
                            {/* o ✓/✕ vai junto do nome do período e não num canto: no
                                canto chocava com o valor quando o trimestre é estreito */}
                            <span style={{ fontSize: '0.62rem', color: t.estado === 'futuro' ? '#94a3b8' : '#64748b', lineHeight: 1.1, marginTop: 2, whiteSpace: 'nowrap' }}>
                                {(t.estado === 'cumpre' || t.estado === 'falha') && (
                                    <span aria-hidden="true" style={{ color: s.corSimbolo, fontWeight: 900, marginRight: 2 }}>{s.simbolo}</span>
                                )}
                                {t.curto}
                            </span>
                        </div>
                    );
                })}
            </div>
            {metaTxt && (
                <div style={{ fontSize: '0.66rem', color: '#64748b', marginTop: 3 }}>
                    meta <strong style={{ color: '#334155' }}>{metaTxt}</strong>
                </div>
            )}
        </div>
    );
};

/* O retrato de todos os objetivos, antes do detalhe: uma barra só, repartida
   pelo que as medições dizem hoje, e a legenda com as contagens. Separada por
   2px de branco entre fatias, para as cores vizinhas nunca se fundirem. */
const COR_BARRA = {
    cumpre: '#0ca30c', risco: '#fab219', fora: '#d03b3b', semdados: '#ec835a',
    aguarda: '#cbd5e1', qualitativa: '#e2e8f0', composta: '#e2e8f0',
};
const ORDEM_PERSPETIVA = ['cumpre', 'risco', 'fora', 'semdados', 'aguarda', 'qualitativa', 'composta'];

const BarraPerspetivas = ({ objetivos, hoje = new Date() }) => {
    const conta = {};
    objetivos.forEach(o => { const e = perspetivaDe(o, hoje).estado; conta[e] = (conta[e] || 0) + 1; });
    const presentes = ORDEM_PERSPETIVA.filter(k => conta[k]);
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 320, paddingBottom: 2 }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Perspetiva a {hoje.toLocaleDateString('pt-PT')} · {objetivos.length} objetivos
            </div>
            <div style={{ display: 'flex', gap: 2, height: 14, borderRadius: 7, overflow: 'hidden', background: '#fff' }}>
                {presentes.map(k => (
                    <div key={k} title={`${ESTADOS_PERSPETIVA[k].label}: ${conta[k]}`}
                         style={{ flex: conta[k], background: COR_BARRA[k] }} />
                ))}
            </div>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                {presentes.map(k => (
                    <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: '#334155' }}>
                        <span style={{ width: 10, height: 10, borderRadius: 3, background: COR_BARRA[k], flexShrink: 0 }} />
                        {ESTADOS_PERSPETIVA[k].label}
                        <strong style={{ color: '#0f172a' }}>{conta[k]}</strong>
                    </span>
                ))}
            </div>
        </div>
    );
};

const ESTADOS = {
    em_curso: { label: 'Em curso', bg: '#e0f2fe', fg: '#075985' },
    atingido: { label: 'Atingido', bg: '#dcfce7', fg: '#166534' },
    nao_atingido: { label: 'Não atingido', bg: '#fee2e2', fg: '#991b1b' },
    // O indicador não pôde ser medido — fórmula inconsistente ou fonte de
    // informação inexistente. É diferente de não ter sido atingido.
    nao_avaliavel: { label: 'Não avaliável', bg: '#f1f5f9', fg: '#475569' },
};
const FREQUENCIAS = ['mensal', 'trimestral', 'semestral', 'anual'];

const campoVazio = {
    politica: '', processo: '', objetivo: '', indicador: '', meta: '', valor_referencia: '',
    frequencia: 'trimestral', responsavel: '', acoes: '', prazo: '', recursos: '',
    fonte_informacao: '', estado: 'em_curso', observacoes: '',
};

/* ---------- Criar / editar objetivo ---------- */
function ObjetivoModal({ obj, ano, onClose, onSaved }) {
    const novo = !obj;
    const [form, setForm] = useState(() => novo ? { ...campoVazio } : {
        ...campoVazio,
        ...Object.fromEntries(Object.keys(campoVazio).map(k => [k, obj[k] ?? ''])),
    });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    const guardar = async () => {
        if (!form.objetivo.trim()) { setErr('Indique o objetivo.'); return; }
        setSaving(true); setErr('');
        try {
            const payload = { ...form, ano };
            if (novo) await api.post('/sistema-integrado/objetivos', payload);
            else await api.put(`/sistema-integrado/objetivos/${obj.id}`, payload);
            onSaved(); onClose();
        } catch (e) {
            setErr(e.response?.data?.error || 'Erro ao guardar');
        } finally { setSaving(false); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 720 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0 }}>{novo ? `Novo objetivo (${ano})` : 'Editar objetivo'}</h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>

                <div style={grid2}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Processo</label>
                        <input value={form.processo} onChange={e => upd('processo', e.target.value)} placeholder="ex.: Comercial, Implementação" style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Estado</label>
                        <select value={form.estado} onChange={e => upd('estado', e.target.value)} style={{ ...inp, width: '100%' }}>
                            {Object.entries(ESTADOS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                    </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label data-liquid-label="true" style={lbl}>Objetivo operacional *</label>
                    <textarea value={form.objetivo} onChange={e => upd('objetivo', e.target.value)} rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label data-liquid-label="true" style={lbl}>Indicador (como se mede)</label>
                    <textarea value={form.indicador} onChange={e => upd('indicador', e.target.value)} rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>
                <div style={grid3}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Meta</label>
                        <input value={form.meta} onChange={e => upd('meta', e.target.value)} placeholder="ex.: ≥90%, 0, >4" style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Valor de referência (ano anterior)</label>
                        <input value={form.valor_referencia} onChange={e => upd('valor_referencia', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Monitorização</label>
                        <select value={form.frequencia} onChange={e => upd('frequencia', e.target.value)} style={{ ...inp, width: '100%' }}>
                            <option value="">—</option>
                            {FREQUENCIAS.map(f => <option key={f} value={f}>{f}</option>)}
                        </select>
                    </div>
                </div>
                <div style={grid2}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Responsável</label>
                        <input value={form.responsavel} onChange={e => upd('responsavel', e.target.value)} placeholder="ex.: GR | RSI" style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Prazo de implementação</label>
                        <input value={form.prazo} onChange={e => upd('prazo', e.target.value)} placeholder="ex.: 31.12.2026" style={{ ...inp, width: '100%' }} />
                    </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label data-liquid-label="true" style={lbl}>Ações</label>
                    <textarea value={form.acoes} onChange={e => upd('acoes', e.target.value)} rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>
                <div style={grid2}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Recursos</label>
                        <textarea value={form.recursos} onChange={e => upd('recursos', e.target.value)} rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Fonte de informação</label>
                        <textarea value={form.fonte_informacao} onChange={e => upd('fonte_informacao', e.target.value)} rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                    </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label data-liquid-label="true" style={lbl}>Política relacionada</label>
                    <textarea value={form.politica} onChange={e => upd('politica', e.target.value)} rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} placeholder="Compromisso da política da qualidade/SST a que este objetivo responde" />
                </div>
                <div style={{ marginBottom: 6 }}>
                    <label data-liquid-label="true" style={lbl}>Observações</label>
                    <textarea value={form.observacoes} onChange={e => upd('observacoes', e.target.value)} rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>

                {err && <div style={{ color: '#e53e3e', margin: '10px 0', fontSize: '0.85rem' }}>{err}</div>}
                <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                    <button data-liquid-secondary="true" onClick={onClose} style={btnSec}>Cancelar</button>
                    <button data-liquid-primary="true" onClick={guardar} disabled={saving} style={{ ...btnPri, opacity: saving ? 0.6 : 1 }}>{saving ? 'A guardar…' : 'Guardar'}</button>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Medições ---------- */
function MedicoesModal({ obj, onClose, onChanged }) {
    const [medicoes, setMedicoes] = useState(obj.medicoes || []);
    const [periodo, setPeriodo] = useState('');
    const [valor, setValor] = useState('');
    const [comentario, setComentario] = useState('');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    const adicionar = async () => {
        if (!periodo.trim()) { setErr('Indique o período (ex.: 1ºT, Março, Anual).'); return; }
        setSaving(true); setErr('');
        try {
            const r = await api.post(`/sistema-integrado/objetivos/${obj.id}/medicoes`, { periodo, valor, comentario });
            setMedicoes(prev => [...prev, r.data]);
            setPeriodo(''); setValor(''); setComentario('');
            onChanged();
        } catch (e) {
            setErr(e.response?.data?.error || 'Erro ao registar');
        } finally { setSaving(false); }
    };

    const remover = async (id) => {
        if (!window.confirm('Eliminar esta medição?')) return;
        try {
            await api.delete(`/sistema-integrado/objetivos/medicoes/${id}`);
            setMedicoes(prev => prev.filter(m => m.id !== id));
            onChanged();
        } catch { alert('Erro ao eliminar'); }
    };

    /* Fechar um período sem medição. A justificação é obrigatória: é ela que
       distingue um período apreciado e sem valor de um período esquecido. */
    const [aFechar, setAFechar] = useState(null);   // período a fechar
    const [razao, setRazao] = useState('');

    const fechar = async () => {
        if (!razao.trim()) { setErr('Escreva a razão por que o período não tem medição.'); return; }
        setSaving(true); setErr('');
        try {
            const r = await api.post(`/sistema-integrado/objetivos/${obj.id}/medicoes`,
                { periodo: aFechar, valor: NAO_MEDIDO, comentario: razao.trim() });
            setMedicoes(prev => [...prev, r.data]);
            setAFechar(null); setRazao('');
            onChanged();
        } catch (e) {
            setErr(e.response?.data?.error || 'Erro ao fechar o período');
        } finally { setSaving(false); }
    };

    const esperados = periodosDe(obj.frequencia);
    const porPeriodo = Object.fromEntries(medicoes.map(m => [m.periodo, m]));

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 560 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0 }}>Medições</h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>
                <div style={{ color: '#64748b', fontSize: '0.88rem', marginBottom: 4 }}>{obj.objetivo}</div>
                <div style={{ color: '#334155', fontSize: '0.85rem', marginBottom: 12 }}>
                    <strong>Indicador:</strong> {obj.indicador || '—'} · <strong>Meta:</strong> {obj.meta || '—'}
                </div>

                {medicoes.length === 0 ? <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Sem medições registadas.</p> : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.87rem', marginBottom: 12 }}>
                        <thead>
                            <tr style={{ textAlign: 'left', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>
                                <th style={{ padding: '4px 6px' }}>Período</th>
                                <th style={{ padding: '4px 6px' }}>Valor</th>
                                <th style={{ padding: '4px 6px' }}>Comentário</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {medicoes.map(m => (
                                <tr key={m.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                                    <td style={{ padding: '5px 6px', fontWeight: 600 }}>{m.periodo}</td>
                                    <td style={{ padding: '5px 6px' }}>{m.valor || '—'}</td>
                                    <td style={{ padding: '5px 6px', color: '#64748b' }}>{m.comentario || ''}</td>
                                    <td style={{ textAlign: 'right' }}>
                                        <button data-liquid-secondary="true" onClick={() => remover(m.id)} style={{ ...btnSec, padding: '2px 8px', fontSize: '0.75rem', color: '#e53e3e' }} aria-label="Apagar" title="Apagar"><DeleteIcon /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {/* Estado de cada período da frequência declarada, com o fecho à mão */}
                <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12, marginBottom: 12 }}>
                    <label data-liquid-label="true" style={lbl}>Períodos ({obj.frequencia || 'sem frequência declarada'})</label>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        {esperados.map(p => {
                            const m = porPeriodo[p];
                            const fechado = m && eNaoMedido(m);
                            const terminou = periodoTerminou(p, obj.ano, obj.frequencia);
                            const cor = m ? (fechado ? { background: '#e2e8f0', color: '#475569' }
                                : { background: '#dcfce7', color: '#166534' })
                                : terminou ? { background: '#fef3c7', color: '#92400e' }
                                    : { background: '#f1f5f9', color: '#94a3b8' };
                            return (
                                <span data-liquid-badge="true" key={p} title={m ? (m.comentario || '') : (terminou ? 'Período terminado e sem registo' : 'Ainda a decorrer')}
                                    style={{ ...badge, ...cor, fontSize: '0.74rem' }}>
                                    {p}: {m ? (fechado ? 'fechado sem medição' : m.valor || '—')
                                        : (terminou ? 'por fechar' : 'a decorrer')}
                                    {!m && terminou && (
                                        <button data-liquid-secondary="true" onClick={() => { setAFechar(p); setRazao(''); setErr(''); }}
                                            title="Fechar este período sem medição, com justificação"
                                            style={{ ...btnSec, padding: '0 6px', marginLeft: 6, fontSize: '0.72rem' }}>fechar</button>
                                    )}
                                </span>
                            );
                        })}
                    </div>
                    {aFechar && (
                        <div style={{ marginTop: 10, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10 }}>
                            <label data-liquid-label="true" style={lbl}>Porque é que {aFechar} não tem medição</label>
                            <textarea value={razao} onChange={e => setRazao(e.target.value)} rows={3}
                                placeholder="ex.: a fonte de informação não produziu o valor; a ação não chegou a ser executada; indicador não calculável com a fórmula em vigor"
                                style={{ ...inp, width: '100%', resize: 'vertical' }} />
                            <div data-liquid-actions="true" style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                <button data-liquid-primary="true" onClick={fechar} disabled={saving} style={{ ...btnPri, opacity: saving ? 0.6 : 1 }}>
                                    {saving ? 'A fechar…' : `Fechar ${aFechar}`}
                                </button>
                                <button data-liquid-secondary="true" onClick={() => { setAFechar(null); setRazao(''); }} style={btnSec}>Cancelar</button>
                            </div>
                            <div style={{ color: '#64748b', fontSize: '0.76rem', marginTop: 6 }}>
                                O período fica registado como “{NAO_MEDIDO}”. Conta como tratado, não como medido.
                            </div>
                        </div>
                    )}
                </div>

                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Período</label>
                        <input value={periodo} onChange={e => setPeriodo(e.target.value)} placeholder="ex.: 3ºT" style={{ ...inp, width: 100 }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Valor</label>
                        <input value={valor} onChange={e => setValor(e.target.value)} placeholder="ex.: 92%" style={{ ...inp, width: 110 }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 140 }}>
                        <label data-liquid-label="true" style={lbl}>Comentário</label>
                        <input value={comentario} onChange={e => setComentario(e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <button data-liquid-primary="true" onClick={adicionar} disabled={saving} style={{ ...btnPri, opacity: saving ? 0.6 : 1 }}>+ Registar</button>
                </div>
                {err && <div style={{ color: '#e53e3e', marginTop: 8, fontSize: '0.85rem' }}>{err}</div>}
            </div>
        </LiquidDialog>
    );
}

/* ---------- Componente principal ---------- */
const ObjetivosIndicadores = () => {
    const anoAtual = new Date().getFullYear();
    const [ano, setAno] = useState(anoAtual);
    const [anosExistentes, setAnosExistentes] = useState([]);
    const [objetivos, setObjetivos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editar, setEditar] = useState(null);     // null | {obj|undefined}
    const [medicoes, setMedicoes] = useState(null); // objetivo
    const [duplicando, setDuplicando] = useState(false);

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const [r, ra] = await Promise.all([
                api.get('/sistema-integrado/objetivos', { params: { ano } }),
                api.get('/sistema-integrado/objetivos/anos'),
            ]);
            setObjetivos(r.data || []);
            setAnosExistentes(Array.isArray(ra.data) ? ra.data : []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [ano]);
    useEffect(() => { carregar(); }, [carregar]);

    const anos = [];
    for (let a = anoAtual + 1; a >= anoAtual - 4; a--) anos.push(a);
    anosExistentes.forEach(a => { if (!anos.includes(a)) anos.push(a); });
    anos.sort((x, y) => y - x);

    const eliminar = async (o) => {
        if (!window.confirm(`Eliminar o objetivo "${o.objetivo}" e as suas medições?`)) return;
        try { await api.delete(`/sistema-integrado/objetivos/${o.id}`); carregar(); }
        catch { alert('Erro ao eliminar'); }
    };

    const duplicarAnoAnterior = async () => {
        const de = ano - 1;
        if (!window.confirm(`Copiar os objetivos de ${de} para ${ano} (sem medições)?`)) return;
        setDuplicando(true);
        try {
            const r = await api.post('/sistema-integrado/objetivos/duplicar-ano', { de, para: ano });
            alert(r.data.message);
            carregar();
        } catch (e) { alert(e.response?.data?.error || 'Erro ao duplicar'); }
        finally { setDuplicando(false); }
    };

    const n = (estado) => objetivos.filter(o => o.estado === estado).length;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Ano</label>
                        <select value={ano} onChange={e => setAno(parseInt(e.target.value, 10))} style={inp}>
                            {anos.map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                    </div>
                    {objetivos.length > 0 && (
                        <div style={{ display: 'flex', gap: 8, paddingBottom: 4 }}>
                            {Object.entries(ESTADOS).map(([k, v]) => (
                                <span data-liquid-tone={liquidTone(v.bg)} data-liquid-badge="true" key={k} style={{ ...badge, background: v.bg, color: v.fg }}>{n(k)} {v.label.toLowerCase()}</span>
                            ))}
                        </div>
                    )}
                    {/* A perspetiva de todos, antes do detalhe: quantos vão bem, quantos
                        estão em risco e — o mais comum — quantos ainda não têm números
                        para se poder dizer. O estado ao lado é o que se declarou; este
                        é o que as medições dizem hoje. */}
                    {objetivos.length > 0 && <BarraPerspetivas objetivos={objetivos} />}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    {objetivos.length === 0 && !loading && (
                        <button data-liquid-secondary="true" onClick={duplicarAnoAnterior} disabled={duplicando} style={btnSec}>
                            <LiquidText>{duplicando ? 'A copiar…' : `⧉ Copiar objetivos de ${ano - 1}`}</LiquidText>
                        </button>
                    )}
                    <button data-liquid-primary="true" onClick={() => setEditar({})} style={btnPri}>+ Novo objetivo</button>
                </div>
            </div>

            {loading ? <p style={{ color: '#718096' }}>A carregar…</p>
                : objetivos.length === 0 ? (
                    <div style={{ padding: '48px 0', textAlign: 'center', color: '#94a3b8' }}>
                        Sem objetivos em {ano}. Crie um novo ou copie os do ano anterior.
                    </div>
                ) : (
                    <div className="table-responsive" style={{ backgroundColor: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
                        <table className="standard-table">
                            <thead>
                                <tr>
                                    <th>Processo</th>
                                    <th>Objetivo</th>
                                    <th>Indicador</th>
                                    <th style={{ textAlign: 'center' }}>Meta</th>
                                    <th>Monit.</th>
                                    <th>Resp.</th>
                                    <th>Medições</th>
                                    <th>Perspetiva</th>
                                    <th>Estado</th>
                                    <th style={{ textAlign: 'right' }}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {objetivos.map(o => {
                                    const es = ESTADOS[o.estado] || ESTADOS.em_curso;
                                    const todas = o.medicoes || [];
                                    const justificadas = todas.filter(eNaoMedido).length;
                                    const feitas = todas.length - justificadas;
                                    const esperadas = periodosEsperados(o.frequencia);
                                    // Só se assinala falta em anos já decorridos — num ano
                                    // a decorrer é natural que ainda faltem períodos. Um período
                                    // fechado com justificação já não é falta.
                                    const emFalta = ano < new Date().getFullYear() && todas.length < esperadas;
                                    return (
                                        <tr key={o.id}>
                                            <td style={{ whiteSpace: 'nowrap', color: '#475569' }}>{o.processo || '—'}</td>
                                            <td style={{ fontWeight: 600, maxWidth: 240 }}>{o.objetivo}</td>
                                            <td style={{ maxWidth: 220, color: '#475569', fontSize: '0.85rem' }}>{o.indicador || '—'}</td>
                                            <td style={{ textAlign: 'center', fontWeight: 700, whiteSpace: 'nowrap' }}>{o.meta || '—'}</td>
                                            <td style={{ whiteSpace: 'nowrap' }}>{o.frequencia || '—'}</td>
                                            <td style={{ whiteSpace: 'nowrap', fontSize: '0.82rem' }}>{o.responsavel || '—'}</td>
                                            <td>
                                                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 180, alignItems: 'center' }}>
                                                    <span data-liquid-tone={liquidTone(emFalta ? '#fef3c7' : justificadas ? '#e2e8f0' : feitas ? '#dcfce7' : '#f1f5f9')} data-liquid-badge="true" title={`${feitas} de ${esperadas} períodos medidos`
                                                        + (justificadas ? `, ${justificadas} fechado(s) sem medição com justificação` : '')
                                                        + `, segundo a frequência ${o.frequencia || 'declarada'}`}
                                                        style={{
                                                            ...badge, fontSize: '0.72rem', fontWeight: 700,
                                                            background: emFalta ? '#fef3c7' : justificadas ? '#e2e8f0' : feitas ? '#dcfce7' : '#f1f5f9',
                                                            color: emFalta ? '#92400e' : justificadas ? '#475569' : feitas ? '#166534' : '#94a3b8',
                                                        }}>
                                                        {feitas}/{esperadas}{justificadas ? ` +${justificadas}` : ''}
                                                    </span>
                                                    {todas.map(m => (
                                                        <span data-liquid-tone={liquidTone(eNaoMedido(m) ? '#f8fafc' : '#f1f5f9')} data-liquid-badge="true" key={m.id} title={m.comentario || ''}
                                                            style={{
                                                                ...badge, fontSize: '0.72rem',
                                                                background: eNaoMedido(m) ? '#f8fafc' : '#f1f5f9',
                                                                color: eNaoMedido(m) ? '#94a3b8' : '#334155',
                                                                fontStyle: eNaoMedido(m) ? 'italic' : 'normal',
                                                            }}>
                                                            {m.periodo}: {m.valor || '—'}
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>
                                            {(() => {
                                                const pv = perspetivaDe(o);
                                                return (
                                                    <td title={pv.razao} style={{ minWidth: 214 }}>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: 204 }}>
                                                            <ChipPerspetiva estado={pv.estado} titulo={pv.razao} tendencia={tendenciaDe(pv)} />
                                                            <AnoDoObjetivo o={o} p={pv} />
                                                        </div>
                                                    </td>
                                                );
                                            })()}
                                            <td><span data-liquid-tone={liquidTone(es.bg)} data-liquid-badge="true" style={{ ...badge, background: es.bg, color: es.fg }}><LiquidText>{es.label}</LiquidText></span></td>
                                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                <button data-liquid-primary="true" onClick={() => setMedicoes(o)} style={{ ...btnPri, ...mini }} title="Registar/consultar medições">Medições</button>
                                                <button data-liquid-secondary="true" onClick={() => setEditar({ obj: o })} style={{ ...btnSec, ...mini }} title="Editar">Editar</button>
                                                <button data-liquid-secondary="true" onClick={() => eliminar(o)} style={{ ...btnSec, ...mini, color: '#e53e3e' }} title="Eliminar"><DeleteIcon /></button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

            {editar && <ObjetivoModal obj={editar.obj} ano={ano} onClose={() => setEditar(null)} onSaved={carregar} />}
            {medicoes && <MedicoesModal obj={medicoes} onClose={() => setMedicoes(null)} onChanged={carregar} />}
        </div>
    );
};

/* ---------- estilos ---------- */
const lbl = { display: 'block', fontSize: '0.74rem', color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em' };
const inp = { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' };
const badge = { padding: '3px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' };
const btnSec = { padding: '7px 14px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', color: '#334155' };
const btnPri = { padding: '7px 14px', border: '1px solid #3182ce', background: '#3182ce', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem' };
const mini = { padding: '5px 10px', marginLeft: 6, fontSize: '0.78rem' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '40px 16px', overflowY: 'auto' };
const modal = { background: '#fff', borderRadius: 14, padding: 22, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' };
const modalHead = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 };
const closeBtn = { border: 'none', background: 'transparent', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 };
const grid3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 };

export default ObjetivosIndicadores;
